import React from 'react';
import { Modal, Button, Card, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCrown, 
  faZap, 
  faUsers, 
  faTimes,
  faArrowRight,
  faCheckCircle,
  faCreditCard,
  faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';

interface SubscriptionConfirmModalProps {
  show: boolean;
  onHide: () => void;
  onConfirm: () => void;
  loading: boolean;
  isUpgrade: boolean;
  currentPlan: string;
  newPlan: string;
  price: number;
  prorationCredit?: number;
  newPlanCharge?: number;
  totalCharge?: number;
  isExistingCustomer: boolean;
}

const SubscriptionConfirmModal: React.FC<SubscriptionConfirmModalProps> = ({
  show,
  onHide,
  onConfirm,
  loading,
  isUpgrade,
  currentPlan,
  newPlan,
  price,
  prorationCredit = 0,
  newPlanCharge = 0,
  totalCharge = 0,
  isExistingCustomer
}) => {
  const getPlanIcon = (plan: string) => {
    switch (plan.toLowerCase()) {
      case 'essential':
        return faUsers;
      case 'professional':
        return faZap;
      case 'premium':
        return faCrown;
      default:
        return faZap;
    }
  };

  const getPlanColor = (plan: string) => {
    switch (plan.toLowerCase()) {
      case 'essential':
        return '#3b82f6';
      case 'professional':
        return '#10b981';
      case 'premium':
        return '#8b5cf6';
      default:
        return '#10b981';
    }
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide}
      centered
      backdrop="static"
      size="lg"
      className="subscription-confirm-modal"
    >
      <div 
        style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.95) 100%)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(0, 212, 255, 0.1)',
          borderRadius: '20px',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)'
        }}
      >
        <Modal.Header 
          className="border-0 pb-0"
          style={{ background: 'transparent', padding: '1.5rem 1.5rem 0 1.5rem' }}
        >
          <div className="w-100 d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <div 
                className="rounded-circle d-flex align-items-center justify-content-center me-3"
                style={{
                  width: '48px',
                  height: '48px',
                  background: `linear-gradient(135deg, ${getPlanColor(newPlan)} 0%, ${getPlanColor(newPlan)}CC 100%)`,
                  boxShadow: `0 0 20px ${getPlanColor(newPlan)}40`
                }}
              >
                <FontAwesomeIcon 
                  icon={getPlanIcon(newPlan)}
                  className="text-white"
                  size="lg"
                />
              </div>
              <div>
                <h4 
                  className="mb-1"
                  style={{
                    color: '#1f2937',
                    fontWeight: 600,
                    fontSize: '1.5rem'
                  }}
                >
                  {isUpgrade ? 'Upgrade Confirmation' : isExistingCustomer ? 'New Subscription Setup' : 'Subscribe Now'}
                </h4>
                <p className="mb-0 text-muted">
                  {isUpgrade ? 'Confirm your plan upgrade' : 'Setup your new subscription'}
                </p>
              </div>
            </div>
            
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={onHide}
              disabled={loading}
              className="rounded-circle p-2"
              style={{
                width: '40px',
                height: '40px',
                border: 'none',
                background: 'rgba(243, 244, 246, 0.8)'
              }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </Button>
          </div>
        </Modal.Header>

        <Modal.Body className="px-5 py-4">
          {/* Plan Transition Visual */}
          {isUpgrade && (
            <Card 
              className="mb-4 border-0"
              style={{
                background: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(5px)',
                borderRadius: '16px',
                border: '1px solid rgba(229, 231, 235, 0.6)'
              }}
            >
              <Card.Body className="p-4">
                <div className="d-flex align-items-center justify-content-center">
                  {/* Current Plan */}
                  <div className="text-center">
                    <div 
                      className="rounded-circle d-inline-flex align-items-center justify-content-center mb-2"
                      style={{
                        width: '40px',
                        height: '40px',
                        background: `linear-gradient(135deg, ${getPlanColor(currentPlan)} 0%, ${getPlanColor(currentPlan)}CC 100%)`,
                        opacity: 0.7
                      }}
                    >
                      <FontAwesomeIcon 
                        icon={getPlanIcon(currentPlan)}
                        className="text-white"
                        size="sm"
                      />
                    </div>
                    <div>
                      <div className="fw-semibold" style={{ fontSize: '14px', color: '#6b7280' }}>
                        Current
                      </div>
                      <div className="fw-bold" style={{ fontSize: '16px', color: '#1f2937' }}>
                        {currentPlan}
                      </div>
                    </div>
                  </div>
                  
                  {/* Arrow */}
                  <div className="mx-4">
                    <FontAwesomeIcon 
                      icon={faArrowRight}
                      style={{
                        fontSize: '24px',
                        color: '#10b981',
                        filter: 'drop-shadow(0 0 8px rgba(16, 185, 129, 0.3))'
                      }}
                    />
                  </div>
                  
                  {/* New Plan */}
                  <div className="text-center">
                    <div 
                      className="rounded-circle d-inline-flex align-items-center justify-content-center mb-2"
                      style={{
                        width: '40px',
                        height: '40px',
                        background: `linear-gradient(135deg, ${getPlanColor(newPlan)} 0%, ${getPlanColor(newPlan)}CC 100%)`,
                        boxShadow: `0 0 15px ${getPlanColor(newPlan)}40`
                      }}
                    >
                      <FontAwesomeIcon 
                        icon={getPlanIcon(newPlan)}
                        className="text-white"
                        size="sm"
                      />
                    </div>
                    <div>
                      <div className="fw-semibold" style={{ fontSize: '14px', color: '#10b981' }}>
                        Upgrading to
                      </div>
                      <div className="fw-bold" style={{ fontSize: '16px', color: '#1f2937' }}>
                        {newPlan}
                      </div>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          )}

          {/* Billing Details */}
          <Card 
            className="mb-4 border-0"
            style={{
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(5px)',
              borderRadius: '16px',
              border: '1px solid rgba(229, 231, 235, 0.6)'
            }}
          >
            <Card.Body className="p-5">
              <div className="d-flex align-items-center mb-3">
                <FontAwesomeIcon 
                  icon={faCreditCard}
                  className="me-2"
                  style={{ color: '#10b981' }}
                />
                <h6 className="mb-0 fw-bold" style={{ color: '#1f2937' }}>
                  Billing Details
                </h6>
              </div>
              
              {isUpgrade && prorationCredit !== 0 && (
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span style={{ color: '#6b7280' }}>Credit for unused time:</span>
                  <span className="fw-semibold" style={{ color: '#ef4444' }}>
                    -${Math.abs(prorationCredit).toFixed(2)}
                  </span>
                </div>
              )}
              
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span style={{ color: '#6b7280' }}>
                  {isUpgrade ? 'New plan charge:' : `${newPlan} Plan (monthly):`}
                </span>
                <span className="fw-semibold" style={{ color: '#1f2937' }}>
                  ${isUpgrade ? newPlanCharge.toFixed(2) : price.toFixed(2)}
                </span>
              </div>
              
              <hr style={{ margin: '16px 0', border: '1px solid rgba(229, 231, 235, 0.8)' }} />
              
              <div className="d-flex justify-content-between align-items-center">
                <span className="fw-bold" style={{ color: '#1f2937', fontSize: '18px' }}>
                  Total due today:
                </span>
                <span 
                  className="fw-bold"
                  style={{ 
                    color: '#10b981', 
                    fontSize: '24px',
                    textShadow: '0 0 10px rgba(16, 185, 129, 0.3)'
                  }}
                >
                  ${isUpgrade ? totalCharge.toFixed(2) : price.toFixed(2)}
                </span>
              </div>
            </Card.Body>
          </Card>

          {/* Important Notes */}
          <Card 
            className="border-0"
            style={{
              background: 'rgba(239, 246, 255, 0.8)',
              backdropFilter: 'blur(5px)',
              borderRadius: '12px',
              border: '1px solid rgba(147, 197, 253, 0.5)'
            }}
          >
            <Card.Body className="p-4">
              <div className="d-flex align-items-start">
                <FontAwesomeIcon 
                  icon={faCheckCircle}
                  className="me-2 mt-1"
                  style={{ color: '#3b82f6', fontSize: '14px' }}
                />
                <div>
                  <div className="fw-semibold mb-1" style={{ color: '#1e40af', fontSize: '14px' }}>
                    {isUpgrade ? 'Instant Upgrade' : 'Subscription Benefits'}
                  </div>
                  <div style={{ color: '#1e3a8a', fontSize: '13px', lineHeight: '1.4' }}>
                    {isUpgrade 
                      ? 'Your account will be upgraded immediately after payment. Your card will be charged today.'
                      : isExistingCustomer
                      ? 'Your previous subscription will be replaced with this new plan. Billing starts immediately.'
                      : 'Your subscription starts immediately after payment. Cancel anytime from your settings.'
                    }
                  </div>
                  
                  <div className="d-flex align-items-center mt-2" style={{ color: '#1e3a8a', fontSize: '12px' }}>
                    <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                    Next billing: {new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()}
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Modal.Body>

        <Modal.Footer 
          className="border-0 pt-0"
          style={{ background: 'transparent', padding: '0 1.5rem 1.5rem 1.5rem' }}
        >
          <div className="w-100 d-flex gap-3">
            <Button
              variant="outline-secondary"
              size="lg"
              onClick={onHide}
              disabled={loading}
              style={{
                flex: '1',
                borderRadius: '12px',
                padding: '12px',
                fontWeight: 600,
                background: 'rgba(243, 244, 246, 0.8)',
                border: '1px solid rgba(209, 213, 219, 0.8)',
                color: '#374151',
                textAlign: 'center',
                display: 'block'
              }}
            >
              Cancel
            </Button>
            
            <Button
              variant="primary"
              size="lg"
              onClick={onConfirm}
              disabled={loading}
              style={{
                flex: '2',
                borderRadius: '12px',
                padding: '12px',
                fontWeight: 600,
                background: `linear-gradient(135deg, ${getPlanColor(newPlan)} 0%, ${getPlanColor(newPlan)}CC 100%)`,
                border: 'none',
                boxShadow: `0 4px 12px ${getPlanColor(newPlan)}40`
              }}
            >
              {loading ? (
                <>
                  <div 
                    className="spinner-border spinner-border-sm me-2"
                    style={{ width: '16px', height: '16px' }}
                  />
                  Processing...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faCreditCard} className="me-2" />
                  {isUpgrade ? `Upgrade to ${newPlan}` : `Subscribe to ${newPlan}`}
                </>
              )}
            </Button>
          </div>
        </Modal.Footer>
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
          .subscription-confirm-modal .modal-content {
            background: transparent !important;
            border: none !important;
            box-shadow: none !important;
          }
          
          .subscription-confirm-modal .modal-dialog {
            margin: 2rem auto;
          }
          
          @media (max-width: 768px) {
            .subscription-confirm-modal .modal-dialog {
              margin: 1rem;
              max-width: calc(100% - 2rem);
            }
          }
        `
      }} />
    </Modal>
  );
};

export default SubscriptionConfirmModal;